import { Dialog } from "@headlessui/react";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../../contexts/LanguageContext";
import { sendCustomerPass } from "../../api/mutations";
import { Button } from "../../components/Button";

// Staff-facing re-send of the wallet pass by email. Email is optional at
// signup (stores may require phone instead), so when the customer has no
// email the staff supplies one here; when they do, we only confirm the
// address the pass will go to — it is never overwritten from this flow.
export default function SendPassModal({ isOpen, onClose, customer, storeId }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);

  const hasEmail = !!customer?.email;

  const sendPassMutation = useMutation({
    mutationFn: sendCustomerPass,
    onSuccess: (data) => {
      // Prefix match: CustomerShow keys the customer by whatever reference the
      // URL carried (uuid, or an old numeric id), not necessarily customer.id.
      queryClient.invalidateQueries({ queryKey: ["customer", storeId] });
      queryClient.invalidateQueries({ queryKey: ["customers", storeId] });
      setEmail("");
      setError(null);
      onClose({ sentTo: data.email });
    },
    onError: (err) => {
      setError(
        err.response?.data?.errors?.join(", ") || t("customers.sendPass.error"),
      );
    },
  });

  const handleSend = () => {
    if (!hasEmail && !email.trim()) {
      setError(t("customers.sendPass.emailRequired"));
      return;
    }

    sendPassMutation.mutate({
      storeId,
      customerId: customer.id,
      email: hasEmail ? undefined : email.trim(),
    });
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:size-10">
                <EnvelopeIcon
                  aria-hidden="true"
                  className="size-6 text-blue-600"
                />
              </div>
              <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                <Dialog.Title
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  {t("customers.sendPass.title")}
                </Dialog.Title>
                <div className="mt-2">
                  {hasEmail ? (
                    <p className="text-sm text-gray-500">
                      {t("customers.sendPass.confirmExisting", {
                        email: (
                          <span className="font-medium text-gray-900">
                            {customer.email}
                          </span>
                        ),
                      })}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">
                        {t("customers.sendPass.emailPrompt")}
                      </p>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("customers.sendPass.emailPlaceholder")}
                        className="mt-3 w-full rounded-md border border-gray-300 p-2"
                      />
                    </>
                  )}
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <Button
                variant="primary"
                onClick={handleSend}
                isSaving={sendPassMutation.isPending}
              >
                {t("customers.sendPass.send")}
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
